import { useState, useEffect, useRef } from 'react'
import { useOnboardingContext } from '../contexts/OnboardingContext'
import { StepFormData, UseOnboardingReturn } from '../types/onboarding.types'

export const useOnboardingStep = (stepKey: string): UseOnboardingReturn => {
  const {
    progress,
    isLoading: contextLoading,
    error: contextError,
    completeStep: contextCompleteStep,
    enterStep,
    exitStep,
    clearError,
  } = useOnboardingContext()

  const [isCompletingStep, setIsCompletingStep] = useState(false)
  const [stepError, setStepError] = useState<string | null>(null)
  const hasEntered = useRef(false)
  const isMounted = useRef(true)

  // ==========================================
  // ENTRY/EXIT MANAGEMENT
  // ==========================================

  useEffect(() => {
    const handleEnterStep = async () => {
      if (hasEntered.current || !isMounted.current) return

      try {
        await enterStep(stepKey, {
          source: 'component_mount',
          route: window.location.pathname,
          timestamp: new Date().toISOString(),
        })
        hasEntered.current = true
      } catch (error) {
        console.warn(`Error entering step ${stepKey}:`, error)
      }
    }

    handleEnterStep()

    // Cleanup - salir del paso al desmontar
    return () => {
      isMounted.current = false
      if (hasEntered.current) {
        exitStep(stepKey).catch(console.warn)
      }
    }
  }, [stepKey, enterStep, exitStep])

  // ==========================================
  // STEP COMPLETION
  // ==========================================

  const completeStep = async (
    stepData: StepFormData,
    notes?: string
  ): Promise<boolean> => {
    setIsCompletingStep(true)
    setStepError(null)
    clearError()

    try {
      await contextCompleteStep(stepKey, stepData, notes)
      return true
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Error al completar paso'
      setStepError(message)
      return false
    } finally {
      setIsCompletingStep(false)
    }
  }

  // ==========================================
  // COMPUTED VALUES
  // ==========================================

  const currentStep = progress?.steps.find(
    s => s.stepDefinition.stepKey === stepKey
  )
  const isCurrentStep = progress?.currentStepKey === stepKey
  const isCompleted = currentStep?.status === 'completed'
  const canComplete = !isCompleted && isCurrentStep
  const timeSpent = currentStep?.totalTimeSpentSeconds || 0
  const visitCount = currentStep?.visitCount || 0
  const estimatedTime = currentStep?.stepDefinition.estimatedTimeMinutes || 0

  return {
    // Estado
    progress,
    currentStep: currentStep || null,
    isCurrentStep,
    isCompleted,
    canComplete,

    // Loading states
    isLoading: contextLoading || isCompletingStep,
    isCompletingStep,

    // Errores
    error: stepError || contextError,
    clearError: () => {
      setStepError(null)
      clearError()
    },

    // Acciones
    completeStep,

    // Métricas
    timeSpent,
    visitCount,
    estimatedTime,
    isOverTime: timeSpent > estimatedTime * 60,
  }
}
